import React, { useEffect } from 'react';
import { Warning } from '@phosphor-icons/react';
import styles from './styles.module.scss';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastAction {
  label: string;
  variant?: 'primary' | 'secondary';
  onClick?: () => void;
}

interface ToastProps {
  message: string;
  title?: string;
  description?: string;
  type?: ToastType;
  duration?: number;
  action?: ToastAction | ToastAction[];
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({
    message,
    title,
    description,
    type = 'info',
    duration = 3000,
    action,
    onClose,
}) => {
    useEffect(() => {
        if (!Number.isFinite(duration)) {
            return undefined;
        }

        const timer = setTimeout(() => {
            onClose();
        }, duration);

        return () => clearTimeout(timer);
    }, [duration, onClose]);

    return (
        <div className={`${styles.toast} ${styles[type]}`}>
            {type === 'warning' && action ? (
                <div className={styles.warningActionIcon}>
                    <Warning size={32} weight="fill" />
                </div>
            ) : (
                <div className={`${styles.icon} ${styles[`${type}Icon`]}`}>
                    {type === 'success' && '✓'}
                    {type === 'error' && '✕'}
                    {type === 'warning' && '!'}
                    {type === 'info' && 'i'}
                </div>
            )}
            <div className={styles.content}>
                {title && <h3 className={styles.title}>{title}</h3>}
                {(description || message) && (
                    <p className={styles.message}>{description || message}</p>
                )}
                {action && (
                    <div className={styles.actions}>
                        {(Array.isArray(action) ? action : [action]).map((item, index) => (
                            <button
                                key={`${item.label}-${index}`}
                                type="button"
                                className={`${styles.actionButton} ${item.variant === 'secondary' ? styles.secondaryAction : styles.primaryAction}`}
                                onClick={() => {
                                    item.onClick?.();
                                    onClose();
                                }}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            <button className={styles.closeButton} onClick={onClose} aria-label="Close notification">×</button>
        </div>
    );
};

export default Toast; 
